import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/", label: "Tableau de bord", icon: "LayoutDashboard", end: true },
  { to: "/clients", label: "Clients & sites", icon: "Building2" },
  { to: "/check", label: "SIGNA CHECK", icon: "ClipboardCheck" },
  { to: "/care", label: "SIGNA CARE", icon: "ShieldCheck" },
  { to: "/track", label: "SIGNA TRACK", icon: "HardHat" },
  { to: "/devis", label: "Devis", icon: "FileSignature" },
  { to: "/demandes", label: "Demandes clients", icon: "Inbox" },
  { to: "/report", label: "SIGNA REPORT", icon: "FileText" },
  { to: "/stats", label: "Statistiques", icon: "BarChart3" },
  { to: "/settings", label: "Paramètres", icon: "Settings" },
];

function Brand({ onLight }) {
  const img = <img src="/signa-logo.png" alt="SIGNA CLEAN" className="h-9 w-auto" />;
  if (onLight) return <div className="inline-flex items-center rounded-md bg-[#0D2E24] px-2.5 py-1.5">{img}</div>;
  return img;
}

function UserMenu({ user, logout, navigate }) {
  const initials = (user?.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid="user-menu-trigger"
        className="flex items-center gap-2 rounded-full border border-slate-200 bg-white py-1 pl-1 pr-3 hover:bg-slate-50">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-600 text-xs font-bold text-white">{initials}</span>
        <span className="hidden text-left sm:block">
          <span className="block text-sm font-semibold leading-none text-slate-800">{user?.name}</span>
          <span className="block text-[11px] leading-none text-slate-500">{({ admin: "Administrateur", conducteur_travaux: "Conducteur de travaux", commercial: "Commercial", chef_chantier: "Chef de chantier", ouvrier: "Ouvrier" })[user?.role] || user?.role}</span>
        </span>
        <Icons.ChevronDown className="h-4 w-4 text-slate-400" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="truncate text-xs font-normal text-slate-500">{user?.email}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">
          <Icons.Settings className="mr-2 h-4 w-4" /> Paramètres
        </DropdownMenuItem>
        <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="text-rose-600">
          <Icons.LogOut className="mr-2 h-4 w-4" /> Déconnexion
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Layout({ children }) {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const nav = NAV.filter((n) => !n.adminOnly || isAdmin);

  const [trackRequests, setTrackRequests] = useState(0);
  const [clientReq, setClientReq] = useState(0);
  const [signedDevis, setSignedDevis] = useState(0);
  const [careAlerts, setCareAlerts] = useState(0);
  useEffect(() => {
    let active = true;
    const fetchCount = () => {
      Promise.all([api.get("/track/requests/pending-count"), api.get("/invoices-read/pending").catch(() => ({ data: [] }))])
        .then(([r, iv]) => { if (active) setTrackRequests((r.data.count || 0) + (iv.data || []).length); }).catch(() => {});
      api.get("/track/client-requests/count").then((r) => { if (active) setClientReq(r.data.count || 0); }).catch(() => {});
      Promise.all([api.get("/devis/signed-pending"), api.get("/devis/read-pending")])
        .then(([s, r]) => { if (active) setSignedDevis((s.data || []).length + (r.data || []).length); }).catch(() => {});
      api.get("/care/anomalies/count").then((r) => { if (active) setCareAlerts(r.data.total || 0); }).catch(() => {});
    };
    fetchCount();
    const t = setInterval(fetchCount, 60000);
    window.addEventListener("cs-req-changed", fetchCount);
    window.addEventListener("devis-signed-changed", fetchCount);
    return () => { active = false; clearInterval(t); window.removeEventListener("cs-req-changed", fetchCount); window.removeEventListener("devis-signed-changed", fetchCount); };
  }, [pathname]);
  const badgeFor = (to) => (to === "/track" && trackRequests > 0 ? trackRequests
    : to === "/demandes" && clientReq > 0 ? clientReq
    : to === "/devis" && signedDevis > 0 ? signedDevis
    : to === "/care" && careAlerts > 0 ? careAlerts : 0);

  const MOBILE_MAIN = [
    { to: "/", label: "Accueil", icon: "Home", end: true },
    { to: "/clients", label: "Clients", icon: "Users" },
    { to: "/check", label: "CHECK", icon: "ClipboardCheck" },
    { to: "/report", label: "REPORT", icon: "FileText" },
  ];
  const PLUS = [
    { to: "/care", label: "SIGNA CARE", icon: "ShieldCheck" },
    { to: "/track", label: "SIGNA TRACK", icon: "HardHat" },
    { to: "/devis", label: "Devis", icon: "FileSignature" },
    { to: "/demandes", label: "Demandes clients", icon: "Inbox" },
    { to: "/stats", label: "Statistiques", icon: "BarChart3" },
    { to: "/settings", label: "Paramètres", icon: "Settings" },
  ].filter((n) => !n.adminOnly || isAdmin);
  const plusActive = PLUS.some((p) => pathname.startsWith(p.to));

  const navClass = ({ isActive }) =>
    `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
      isActive ? "bg-[#1B4D3E] text-white border-l-4 border-emerald-400 pl-2"
        : "text-slate-300 hover:bg-white/5 hover:text-white border-l-4 border-transparent pl-2"}`;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-[260px] flex-col bg-[#0D2E24] px-4 py-6 lg:flex">
        <div className="px-2"><Brand /></div>
        <div className="mt-2 h-0.5 w-10 rounded bg-emerald-500 mx-2" />
        <nav className="mt-8 flex flex-col gap-1">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className={navClass} data-testid={`nav-${n.icon}`}>
              {(() => { const I = Icons[n.icon]; return <I className="h-[18px] w-[18px]" />; })()}
              <span className="flex-1">{n.label}</span>
              {badgeFor(n.to) > 0 && (
                <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-rose-500 px-1.5 text-[11px] font-bold text-white" data-testid="nav-track-badge">{badgeFor(n.to)}</span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto flex items-center gap-2 px-2 text-xs text-emerald-200/70">
          <Icons.Leaf className="h-4 w-4" />
          Agir pour des espaces plus propres
        </div>
      </aside>

      {/* Main */}
      <div className="lg:pl-[260px]">
        {/* Header */}
        <header className="sticky top-0 z-20 flex h-14 items-center justify-between border-b border-slate-200 bg-white/90 px-4 backdrop-blur lg:h-16 lg:px-8">
          <div className="lg:hidden"><Brand onLight /></div>
          <div className="hidden lg:block" />
          <UserMenu user={user} logout={logout} navigate={navigate} />
        </header>

        <main className="mx-auto max-w-6xl px-4 pb-28 pt-5 lg:px-8 lg:pb-10 lg:pt-8 animate-fade-in">
          {children}
        </main>
      </div>

      {/* Mobile bottom nav : 5 entrées lisibles + menu Plus */}
      <nav className="fixed inset-x-0 bottom-0 z-30 flex items-stretch justify-around border-t border-slate-200 bg-white lg:hidden"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
        {MOBILE_MAIN.map((n) => {
          const I = Icons[n.icon];
          return (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`bottomnav-${n.icon}`}
              className={({ isActive }) =>
                `flex flex-1 flex-col items-center justify-center gap-0.5 py-2 text-[11px] font-medium ${
                  isActive ? "text-emerald-700" : "text-slate-500"}`}>
              <I className="h-5 w-5" />
              <span>{n.label}</span>
            </NavLink>
          );
        })}
        <DropdownMenu>
          <DropdownMenuTrigger data-testid="bottomnav-plus"
            className={`relative flex flex-1 flex-col items-center justify-center gap-0.5 py-2 text-[11px] font-medium focus:outline-none ${plusActive ? "text-emerald-700" : "text-slate-500"}`}>
            <Icons.MoreHorizontal className="h-5 w-5" />
            <span>Plus</span>
            {(trackRequests + signedDevis + careAlerts) > 0 && (
              <span className="absolute right-[22%] top-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white" data-testid="plus-track-badge">{trackRequests + signedDevis + careAlerts}</span>
            )}
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="end" className="mb-1 w-56">
            {PLUS.map((n) => {
              const I = Icons[n.icon];
              return (
                <DropdownMenuItem key={n.to} onClick={() => navigate(n.to)} data-testid={`plus-${n.icon}`}>
                  <I className="mr-2 h-4 w-4" /> <span className="flex-1">{n.label}</span>
                  {badgeFor(n.to) > 0 && <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-rose-500 px-1.5 text-[11px] font-bold text-white">{badgeFor(n.to)}</span>}
                </DropdownMenuItem>
              );
            })}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} data-testid="plus-logout" className="text-rose-600">
              <Icons.LogOut className="mr-2 h-4 w-4" /> Déconnexion
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </nav>
    </div>
  );
}
